import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { ErrorStatus, WarningStatus } from '@sas/react-svg';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useIsRTL } from '../../../locale';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useDesignTokens, useShowFocusContext } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { useDensity } from '../../Density';
import { useDirectSpeech } from '../../DirectSpeech';
import { Icon } from '../../Icon';
import { Input } from '../../Input';
import { getInputDesignTokens } from '../../Input/__internal__/BaseInput';
import { MessagePopover } from '../../Input/__internal__/MessagePopover';
import { GlobalSettingsKeys } from '../../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../../Settings/GlobalSettingsProvider';
import bundle from '../NovaComponents-NumericStepper-gui-icu.properties';
import cn from '../NumericStepper.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useHtmlId = createUseHtmlId('NumericStepperInput-nonEditableTextField');
export const NumericStepperInput = /*#__PURE__*/forwardRef(({
  editMode = 'select',
  readOnly = false,
  style: userStyle,
  ...props
}, ref) => {
  const inputRef = useRef(null);
  const setInputRef = useCallback(element => {
    inputRef.current = element;
    setRef(ref, element);
  }, [ref]);
  const isRTL = useIsRTL();
  const {
    density
  } = useDensity();

  // auto-width
  useEffect(function calculateWidth() {
    const input = inputRef.current;
    const parent = input?.parentElement;
    const container = parent?.parentElement;
    if (input && parent && container) {
      if (userStyle?.width) {
        parent.style.width = '100%';
        return;
      }
      const button = container.querySelector('button');
      const style = window.getComputedStyle(input);
      const fontFamily = style.fontFamily;
      const fontSize = style.fontSize;
      const width = calculateInputWidth(fontSize, fontFamily, props.maxLength);
      const inputWidth = width + parseInt(isRTL ? style.paddingRight : style.paddingLeft) + 20 +
      // The padding-right is 8px when no status icon shown, otherwise it is 20px, and the larger one is reserved.
      parseInt(style.marginLeft) + parseInt(style.marginRight) + parseInt(style.borderLeftWidth) + parseInt(style.borderRightWidth) + 2 +
      // to avoid the blinking caret found, make the width widen 2 pixels.
      1 +
      // status icon take 1px more space which overlaps with input content.
      (button?.offsetWidth ?? 0) * 2; // to place increasing and decreasing buttons

      parent.style.width = parent.style.minWidth = container.style.minWidth = `${inputWidth}px`;
    }
  }, [props.maxLength, userStyle?.width, editMode, readOnly, density, isRTL]);
  if (readOnly) {
    return /*#__PURE__*/_jsx(Input, {
      ...props,
      editable: false,
      ref: setInputRef,
      type: "number",
      role: "spinbutton"
    });
  } else if (editMode === 'combo') {
    return /*#__PURE__*/_jsx(Input, {
      ...props,
      className: cx(cn.textField, props.status && cn.hasStatus),
      editable: true,
      onChange: e => {
        !props.disabled && props.onStateChange?.({
          value: e.value,
          state: 'change'
        });
      },
      onCommit: e => !props.disabled && props.onStateChange?.({
        value: e.value,
        state: e.reason === 'suggestion' || e.reason === undefined ? 'blur' : e.reason
      }),
      onFocus: () => !props.disabled && props.onStateChange?.({
        value: props.value,
        state: 'focus'
      }),
      ref: setInputRef,
      role: "spinbutton",
      type: "number",
      variant: "standard"
    });
  } else {
    return /*#__PURE__*/_jsx(NonEditableTextField, {
      ...props,
      ref: setInputRef
    });
  }
});
export const NonEditableTextField = /*#__PURE__*/forwardRef((props, ref) => {
  const localize = useTextLocalizer();
  const {
    density
  } = useDensity();
  const dt = useDesignTokens();
  const {
    isKeyboardUser
  } = useShowFocusContext() ?? false;
  const statusMessageId = useHtmlId();
  const directSpeech = useDirectSpeech();
  const overrideTokens = isKeyboardUser ? {
    '--input-focus-bd': 'var(--numeric-stepper-input-bd)'
  } : {};
  const [hovering, setHovering] = useState(false);
  const [focused, setFocused] = useState(false);
  const divRef = useRef(null);
  const startHovering = useCallback(() => setHovering(true), []);
  const endHovering = useCallback(() => setHovering(false), []);
  const isRTL = useIsRTL();
  let statusMessage = '';
  if (props.status) {
    if (focused && props.message) {
      statusMessage = isCustomMessage(props.message) ? props.message.message : props.message;
    } else if (props.ariaLabel) {
      if (props.status === 'warning') {
        statusMessage = localize('invalidEntryWarning.aria', {
          label: props.ariaLabel
        });
      } else {
        statusMessage = localize('invalidEntryError.aria', {
          label: props.ariaLabel
        });
      }
    }
  }
  useEffect(function announceStatusMessage() {
    if (props.status) {
      directSpeech?.notify(statusMessage);
    }
  }, [props.status, directSpeech, statusMessage, props.value]);
  const inputTokens = getInputDesignTokens(dt, {
    disabled: props.disabled,
    status: props.status,
    editable: false,
    variant: 'standard',
    isCustomFocusTurnedOn: true,
    density
  });
  const shouldPlayAnimation = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true';
  const iconAnimation = !shouldPlayAnimation ? undefined : {
    closed: {
      opacity: 0
    },
    open: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0, 0.5, 0.2, 1]
      }
    },
    exit: {
      opacity: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0.5, 0, 0.8, 1]
      }
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    style: {
      ...inputTokens,
      ...overrideTokens
    },
    className: cn.textField,
    onBlur: () => {
      !props.disabled && props.onStateChange?.({
        value: props.value,
        state: 'blur'
      });
      setFocused(false);
    },
    onFocus: () => setFocused(true),
    children: [/*#__PURE__*/_jsx("div", {
      ref: element => {
        if (element) {
          divRef.current = element;
          setRef(ref, element);
        }
      },
      className: cx(cn.nonEditableTextField, props.disabled && cn.disabled, !!props.status && cn.hasInputIcon, isRTL && cn.rtl),
      dir: "ltr"
      // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
      ,
      role: "spinbutton",
      "aria-disabled": props.disabled,
      "aria-label": props.ariaLabel,
      "aria-labelledby": props.ariaLabelledBy,
      "aria-describedby": (props.ariaDescribedBy ?? []).join(' '),
      "aria-invalid": props.status === 'alert' ? true : undefined,
      tabIndex: 0,
      onMouseEnter: startHovering,
      onMouseLeave: endHovering,
      children: props.value
    }), props.status ? /*#__PURE__*/_jsx("span", {
      "aria-live": "off",
      role: "status",
      style: {
        width: '0',
        overflow: 'hidden'
      },
      children: /*#__PURE__*/_jsx("span", {
        className: cn.offscreen,
        id: statusMessageId,
        children: isCustomMessage(props.message) ? props.message.message : props.message
      })
    }) : null, /*#__PURE__*/_jsx(AnimatePresence, {
      children: !focused && props.status ? /*#__PURE__*/_jsx(motion.div, {
        className: cn.inputIcon,
        onMouseEnter: startHovering,
        onMouseLeave: endHovering,
        variants: iconAnimation,
        initial: "closed",
        animate: "open",
        exit: "exit",
        children: /*#__PURE__*/_jsx(Icon, {
          icon: props.status === 'alert' ? ErrorStatus : WarningStatus,
          disabled: props.disabled,
          testId: postfixTestId(props.testId, 'statusIcon'),
          ...(density === 'high' && {
            style: {
              '--icon-height': dt.tokens['size-icon-s'],
              '--icon-width': dt.tokens['size-icon-s']
            }
          })
        })
      }) : null
    }), props.status ? /*#__PURE__*/_jsx(MessagePopover, {
      status: props.status,
      referenceElementRef: divRef,
      ...(isCustomMessage(props.message) ? props.message : {
        message: props.message ?? ''
      }),
      isOpen: focused || hovering,
      testId: postfixTestId(props.testId, 'messagePopper'),
      hideOnRefHidden: true
      // Want to blur the input to close the status message for popover's onClose events
      ,
      onClose: () => divRef.current?.blur()
    }) : null]
  });
});
function isCustomMessage(message) {
  return message?.message !== undefined;
}
function calculateInputWidth(fontSize, fontFamily, length) {
  const span = document.createElement('span');
  let width = span.offsetWidth;
  span.style.visibility = 'hidden';
  span.style.fontSize = fontSize;
  span.style.fontFamily = fontFamily;
  span.style.display = 'inline-block';
  document.body.appendChild(span);
  const text = new Array(length).fill('0').join('');
  if (typeof span.textContent !== 'undefined') {
    span.textContent = text;
  } else {
    span.innerText = text;
  }
  width = parseFloat(window.getComputedStyle(span).width) - width;
  document.body.removeChild(span);
  return width;
}
//# sourceMappingURL=NumericStepperInput.js.map